// /opt/isp_erp/frontend/src/pages/warehouse/SucursalesBodega.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Typography, Tabs, Tab, Paper, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TablePagination, Button, TextField, MenuItem, Alert, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, Autocomplete, IconButton, CircularProgress, Grid,
} from "@mui/material";
import axios from "../../api/axios";
import { ThemeProvider } from "@mui/material/styles";
import { temaSucursales } from "./sucursalesTema";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlined from "@mui/icons-material/LocalShippingOutlined";
import RouterOutlined from "@mui/icons-material/RouterOutlined";
import SendOutlined from "@mui/icons-material/SendOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import StorefrontOutlined from "@mui/icons-material/StorefrontOutlined";
import LocalOfferOutlined from "@mui/icons-material/LocalOfferOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import { mensajeError } from "../../utils/mensajesError";
import EnviosEquiposPanel from "../../components/warehouse/EnviosEquiposPanel";
import DevolucionesCentralPanel from "../../components/warehouse/DevolucionesCentralPanel";

const API = "/warehouse/sucursales";
const fmtFecha = (v) => (v ? new Date(v).toLocaleString("es-EC") : "—");
const fmtNum = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("es-EC", { maximumFractionDigits: 2 }));
const ESTADO_COLOR = { EN_TRANSITO: "warning", RECIBIDO: "success", ANULADO: "default" };
const ESTADO_TEXTO = { EN_TRANSITO: "En tránsito", RECIBIDO: "Recibido", ANULADO: "Anulado" };

function useDebounced(valor, ms) {
  const [v, setV] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setV(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return v;
}

function descargar(datos, nombre) {
  const url = window.URL.createObjectURL(new Blob([datos], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// ====================== STOCK ======================
function AsignarDialog({ sucursal, material, onClose, onHecho }) {
  const [tecnicos, setTecnicos] = useState([]);
  const [tecnicoId, setTecnicoId] = useState("");
  const [cantidad, setCantidad] = useState("");
  const [obs, setObs] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    axios.get(`${API}/${sucursal.id}/personal`)
      .then((r) => setTecnicos((r.data || []).filter((p) => p.rol === "TECNICO")))
      .catch(() => setTecnicos([]));
  }, [sucursal.id]);

  const guardar = async () => {
    setError("");
    const n = Number(cantidad);
    if (!tecnicoId) { setError("Elige el técnico"); return; }
    if (!Number.isInteger(n) || n < 1) { setError("Escribe una cantidad entera mayor a 0"); return; }
    if (n > material.cantidad) { setError(`Solo hay ${fmtNum(material.cantidad)} ${material.unidad} en la sucursal`); return; }
    setEnviando(true);
    try {
      const r = await axios.post(`${API}/${sucursal.id}/asignar-tecnico`, {
        material_id: material.material_id, tecnico_id: Number(tecnicoId), cantidad: n, observaciones: obs.trim() || null,
      });
      onHecho(r.data.mensaje);
    } catch (e) {
      setError(mensajeError(e, "No se pudo asignar el material"));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open onClose={enviando ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Asignar a un técnico</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {material.nombre} · disponible {fmtNum(material.cantidad)} {material.unidad}
        </Typography>
        {tecnicos.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>Esta sucursal no tiene técnicos. Agrégalos en la pestaña Personal.</Alert>
        )}
        <TextField select fullWidth size="small" label="Técnico" value={tecnicoId} onChange={(e) => setTecnicoId(e.target.value)} sx={{ mb: 2 }}>
          {tecnicos.map((t) => (<MenuItem key={t.user_id} value={t.user_id}>{t.username}</MenuItem>))}
        </TextField>
        <TextField fullWidth size="small" type="number" label="Cantidad" value={cantidad} onChange={(e) => setCantidad(e.target.value)} sx={{ mb: 2 }} inputProps={{ min: 1, step: 1 }} />
        <TextField fullWidth size="small" label="Observaciones (opcional)" value={obs} onChange={(e) => setObs(e.target.value)} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
        <Button variant="contained" onClick={guardar} disabled={enviando}>{enviando ? "Guardando..." : "Asignar"}</Button>
      </DialogActions>
    </Dialog>
  );
}

function DevolverCentralDialog({ sucursal, material, onClose, onHecho }) {
  const [cantidad, setCantidad] = useState("");
  const [obs, setObs] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  const guardar = async () => {
    setError("");
    const n = Number(cantidad);
    if (!Number.isInteger(n) || n < 1) { setError("Escribe una cantidad entera mayor a 0"); return; }
    if (n > material.cantidad) { setError(`Solo hay ${fmtNum(material.cantidad)} ${material.unidad} en la sucursal`); return; }
    setEnviando(true);
    try {
      const r = await axios.post(`${API}/${sucursal.id}/devolver-central`, {
        material_id: material.material_id, cantidad: n, observaciones: obs.trim() || null,
      });
      onHecho(r.data.mensaje);
    } catch (e) {
      setError(mensajeError(e, "No se pudo devolver el material"));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open onClose={enviando ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Devolver a la bodega central</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {material.nombre} · disponible {fmtNum(material.cantidad)} {material.unidad}
        </Typography>
        <TextField fullWidth size="small" type="number" label="Cantidad" value={cantidad} onChange={(e) => setCantidad(e.target.value)} sx={{ mb: 2 }} inputProps={{ min: 1, step: 1 }} />
        <TextField fullWidth size="small" label="Observaciones (opcional)" value={obs} onChange={(e) => setObs(e.target.value)} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
        <Button variant="contained" onClick={guardar} disabled={enviando}>{enviando ? "Guardando..." : "Devolver"}</Button>
      </DialogActions>
    </Dialog>
  );
}

function DevolucionTecnicoDialog({ sucursal, onClose, onHecho }) {
  const [tecnicos, setTecnicos] = useState([]);
  const [tecnicoId, setTecnicoId] = useState("");
  const [stock, setStock] = useState([]);
  const [materialId, setMaterialId] = useState("");
  const [cantidad, setCantidad] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    axios.get(`${API}/${sucursal.id}/personal`)
      .then((r) => setTecnicos((r.data || []).filter((p) => p.rol === "TECNICO")))
      .catch(() => setTecnicos([]));
  }, [sucursal.id]);

  useEffect(() => {
    setStock([]);
    setMaterialId("");
    if (!tecnicoId) return;
    axios.get(`${API}/${sucursal.id}/tecnicos/${tecnicoId}/stock`)
      .then((r) => setStock(r.data || []))
      .catch((e) => setError(mensajeError(e, "No se pudo cargar el material del técnico")));
  }, [tecnicoId, sucursal.id]);

  const elegido = stock.find((s) => s.material_id === materialId);

  const guardar = async () => {
    setError("");
    const n = Number(cantidad);
    if (!tecnicoId || !elegido) { setError("Elige el técnico y el material"); return; }
    if (!Number.isInteger(n) || n < 1) { setError("Escribe una cantidad entera mayor a 0"); return; }
    if (n > elegido.cantidad) { setError(`El técnico solo tiene ${fmtNum(elegido.cantidad)} ${elegido.unidad}`); return; }
    setEnviando(true);
    try {
      const r = await axios.post(`${API}/${sucursal.id}/devolver-tecnico`, {
        material_id: elegido.material_id, tecnico_id: Number(tecnicoId), cantidad: n,
      });
      onHecho(r.data.mensaje);
    } catch (e) {
      setError(mensajeError(e, "No se pudo registrar la devolución"));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open onClose={enviando ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Recibir devolución de un técnico</DialogTitle>
      <DialogContent>
        <TextField select fullWidth size="small" label="Técnico" value={tecnicoId} onChange={(e) => { setError(""); setTecnicoId(e.target.value); }} sx={{ my: 2 }}>
          {tecnicos.map((t) => (<MenuItem key={t.user_id} value={t.user_id}>{t.username}</MenuItem>))}
        </TextField>
        <TextField select fullWidth size="small" label="Material" value={materialId} onChange={(e) => setMaterialId(e.target.value)} disabled={!tecnicoId} sx={{ mb: 2 }}
          helperText={tecnicoId && stock.length === 0 ? "El técnico no tiene material" : " "}>
          {stock.map((s) => (<MenuItem key={s.material_id} value={s.material_id}>{s.nombre} ({fmtNum(s.cantidad)} {s.unidad})</MenuItem>))}
        </TextField>
        <TextField fullWidth size="small" type="number" label="Cantidad" value={cantidad} onChange={(e) => setCantidad(e.target.value)} inputProps={{ min: 1, step: 1 }} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
        <Button variant="contained" onClick={guardar} disabled={enviando}>{enviando ? "Guardando..." : "Registrar"}</Button>
      </DialogActions>
    </Dialog>
  );
}

function StockTab({ sucursal }) {
  const [texto, setTexto] = useState("");
  const q = useDebounced(texto.trim(), 350);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(25);
  const [data, setData] = useState({ items: [], total: 0 });
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [version, setVersion] = useState(0);
  const [asignando, setAsignando] = useState(null);
  const [devolviendo, setDevolviendo] = useState(null);
  const [recibiendo, setRecibiendo] = useState(false);

  useEffect(() => { setPage(0); }, [q, sucursal.id]);

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    setError("");
    axios.get(`${API}/${sucursal.id}/stock`, { params: { q: q || undefined, page: page + 1, page_size: rows } })
      .then((r) => { if (vivo) setData(r.data); })
      .catch((e) => { if (vivo) { setData({ items: [], total: 0 }); setError(mensajeError(e, "No se pudo cargar el stock")); } })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [sucursal.id, q, page, rows, version]);

  const hecho = (mensaje) => {
    setAsignando(null);
    setDevolviendo(null);
    setRecibiendo(false);
    setAviso(mensaje);
    setVersion((v) => v + 1);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
        <TextField size="small" label="Buscar material" value={texto} onChange={(e) => setTexto(e.target.value)} sx={{ minWidth: 260 }} />
        {sucursal.puede_operar && (
          <Button variant="outlined" onClick={() => setRecibiendo(true)}>Recibir devolución de técnico</Button>
        )}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {aviso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAviso("")}>{aviso}</Alert>}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Material</TableCell>
              <TableCell>Unidad</TableCell>
              <TableCell align="right">Cantidad</TableCell>
              {sucursal.puede_operar && <TableCell align="right">Acciones</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {cargando && (<TableRow><TableCell colSpan={5} align="center"><CircularProgress size={20} /></TableCell></TableRow>)}
            {!cargando && data.items.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center">Esta sucursal no tiene material todavía. Se llena al recibir envíos de la bodega central.</TableCell></TableRow>
            )}
            {!cargando && data.items.map((m) => (
              <TableRow key={m.material_id} hover>
                <TableCell>{m.codigo}</TableCell>
                <TableCell>{m.nombre}</TableCell>
                <TableCell>{m.unidad}</TableCell>
                <TableCell align="right">{fmtNum(m.cantidad)}</TableCell>
                {sucursal.puede_operar && (
                  <TableCell align="right">
                    <Button size="small" onClick={() => setAsignando(m)}>Asignar a técnico</Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination component="div" count={data.total} page={page} onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rows} onRowsPerPageChange={(e) => { setRows(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage="Filas" />
      </TableContainer>
      {asignando && <AsignarDialog sucursal={sucursal} material={asignando} onClose={() => setAsignando(null)} onHecho={hecho} />}
      {devolviendo && <DevolverCentralDialog sucursal={sucursal} material={devolviendo} onClose={() => setDevolviendo(null)} onHecho={hecho} />}
      {recibiendo && <DevolucionTecnicoDialog sucursal={sucursal} onClose={() => setRecibiendo(false)} onHecho={hecho} />}
    </Box>
  );
}

// ====================== ENVÍOS ======================
function NuevoEnvioDialog({ ctx, onClose, onHecho }) {
  const [sucursalId, setSucursalId] = useState(ctx.sucursales.length === 1 ? ctx.sucursales[0].id : "");
  const [lineas, setLineas] = useState([]);
  const [textoMat, setTextoMat] = useState("");
  const qMat = useDebounced(textoMat.trim(), 300);
  const [opciones, setOpciones] = useState([]);
  const [entrega, setEntrega] = useState("");
  const [obs, setObs] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    let vivo = true;
    axios.get(`${API}/materiales-disponibles`, { params: { q: qMat || undefined } })
      .then((r) => { if (vivo) setOpciones(r.data || []); })
      .catch(() => { if (vivo) setOpciones([]); });
    return () => { vivo = false; };
  }, [qMat]);

  const agregar = (m) => {
    if (!m) return;
    setLineas((prev) => (prev.some((l) => l.material.id === m.id) ? prev : [...prev, { material: m, cantidad: "" }]));
    setTextoMat("");
  };
  const cambiarCantidad = (id, valor) => setLineas((prev) => prev.map((l) => (l.material.id === id ? { ...l, cantidad: valor } : l)));
  const quitar = (id) => setLineas((prev) => prev.filter((l) => l.material.id !== id));

  const enviar = async () => {
    setError("");
    if (!sucursalId) { setError("Elige la sucursal de destino"); return; }
    if (lineas.length === 0) { setError("Agrega al menos un material"); return; }
    for (const l of lineas) {
      const n = Number(l.cantidad);
      if (!Number.isInteger(n) || n < 1) { setError(`Escribe una cantidad entera mayor a 0 para ${l.material.nombre}`); return; }
      if (n > l.material.stock_bodega) { setError(`${l.material.nombre}: solo hay ${fmtNum(l.material.stock_bodega)} ${l.material.unidad} en la bodega central`); return; }
    }
    setEnviando(true);
    try {
      const r = await axios.post(`${API}/envios`, {
        sucursal_id: Number(sucursalId),
        items: lineas.map((l) => ({ material_id: l.material.id, cantidad: Number(l.cantidad) })),
        entrega_nombre: entrega.trim() || null,
        observaciones: obs.trim() || null,
      });
      onHecho(r.data);
    } catch (e) {
      setError(mensajeError(e, "No se pudo registrar el envío"));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open onClose={enviando ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Nuevo envío a una sucursal</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6}>
            <TextField select fullWidth size="small" label="Sucursal de destino" value={sucursalId} onChange={(e) => setSucursalId(e.target.value)}>
              {ctx.sucursales.map((s) => (<MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="Quién entrega (opcional)" value={entrega} onChange={(e) => setEntrega(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              options={opciones}
              value={null}
              blurOnSelect
              filterOptions={(x) => x}
              getOptionLabel={(o) => `${o.codigo} - ${o.nombre} (${fmtNum(o.stock_bodega)} ${o.unidad})`}
              inputValue={textoMat}
              onInputChange={(_, v, motivo) => { if (motivo === "input") setTextoMat(v); }}
              onChange={(_, v) => agregar(v)}
              noOptionsText="No hay materiales con stock en la bodega central"
              renderInput={(params) => (<TextField {...params} size="small" label="Agregar material (escribe para buscar)" />)}
            />
          </Grid>
        </Grid>
        <TableContainer sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Material</TableCell>
                <TableCell align="right">En bodega</TableCell>
                <TableCell align="right" sx={{ width: 140 }}>Cantidad a enviar</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {lineas.length === 0 && (<TableRow><TableCell colSpan={4} align="center">Aún no hay materiales</TableCell></TableRow>)}
              {lineas.map((l) => (
                <TableRow key={l.material.id}>
                  <TableCell>{l.material.codigo} - {l.material.nombre}</TableCell>
                  <TableCell align="right">{fmtNum(l.material.stock_bodega)} {l.material.unidad}</TableCell>
                  <TableCell align="right">
                    <TextField size="small" type="number" value={l.cantidad} onChange={(e) => cambiarCantidad(l.material.id, e.target.value)} inputProps={{ min: 1, step: 1 }} />
                  </TableCell>
                  <TableCell align="right"><IconButton size="small" onClick={() => quitar(l.material.id)} aria-label="Quitar">✕</IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TextField fullWidth size="small" label="Observaciones (opcional)" value={obs} onChange={(e) => setObs(e.target.value)} sx={{ mt: 2 }} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
        <Button variant="contained" onClick={enviar} disabled={enviando}>{enviando ? "Enviando..." : "Registrar envío"}</Button>
      </DialogActions>
    </Dialog>
  );
}

function DetalleEnvioDialog({ ctx, envioId, onClose, onCambio }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [recibidas, setRecibidas] = useState({});
  const [recibe, setRecibe] = useState("");
  const [obs, setObs] = useState("");
  const [motivo, setMotivo] = useState("");
  const [anulando, setAnulando] = useState(false);
  const [trabajando, setTrabajando] = useState(false);

  useEffect(() => {
    let vivo = true;
    axios.get(`${API}/envios/${envioId}`)
      .then((r) => { if (vivo) setD(r.data); })
      .catch((e) => { if (vivo) setError(mensajeError(e, "No se pudo cargar el envío")); });
    return () => { vivo = false; };
  }, [envioId]);

  if (!d) {
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth="sm">
        <DialogContent sx={{ textAlign: "center", py: 4 }}>
          {error ? <Alert severity="error">{error}</Alert> : <CircularProgress />}
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Cerrar</Button></DialogActions>
      </Dialog>
    );
  }

  const suc = ctx.sucursales.find((s) => s.id === d.sucursal_id);
  const puedeRecibir = d.estado === "EN_TRANSITO" && !!(suc && suc.puede_operar);
  const puedeAnular = d.estado === "EN_TRANSITO" && ctx.puede_enviar;

  const cantidadRecibida = (i) => (recibidas[i.id] !== undefined ? recibidas[i.id] : String(i.cantidad_enviada));

  const descargarActa = async () => {
    try {
      const r = await axios.get(`${API}/envios/${d.id}/pdf`, { responseType: "blob" });
      descargar(r.data, `${d.numero}.pdf`);
    } catch (e) {
      setError("No se pudo descargar el acta");
    }
  };

  const confirmar = async () => {
    setError("");
    const items = d.items.map((i) => ({ item_id: i.id, cantidad_recibida: Number(cantidadRecibida(i)) }));
    for (const it of items) {
      if (!Number.isInteger(it.cantidad_recibida) || it.cantidad_recibida < 0) { setError("Las cantidades recibidas deben ser números enteros (0 o más)"); return; }
    }
    setTrabajando(true);
    try {
      const r = await axios.post(`${API}/envios/${d.id}/recibir`, { items, recibe_nombre: recibe.trim() || null, observaciones: obs.trim() || null });
      setD(r.data);
      onCambio("Recepción registrada");
    } catch (e) {
      setError(mensajeError(e, "No se pudo registrar la recepción"));
    } finally {
      setTrabajando(false);
    }
  };

  const anular = async () => {
    setError("");
    if (motivo.trim().length < 3) { setError("Indica el motivo de la anulación"); return; }
    setTrabajando(true);
    try {
      const r = await axios.post(`${API}/envios/${d.id}/anular`, { motivo: motivo.trim() });
      setD(r.data);
      setAnulando(false);
      onCambio("Envío anulado: el material volvió a la bodega central");
    } catch (e) {
      setError(mensajeError(e, "No se pudo anular el envío"));
    } finally {
      setTrabajando(false);
    }
  };

  return (
    <Dialog open onClose={trabajando ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {d.numero} <Chip size="small" sx={{ ml: 1 }} color={ESTADO_COLOR[d.estado]} label={ESTADO_TEXTO[d.estado] || d.estado} />
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2">Destino: <strong>{d.sucursal_nombre}</strong></Typography>
        <Typography variant="body2">Enviado el {fmtFecha(d.fecha_envio)} por {d.enviado_por || "—"} · entrega: {d.entrega_nombre || "—"}</Typography>
        {d.observaciones && <Typography variant="body2">Observaciones: {d.observaciones}</Typography>}
        {d.estado === "RECIBIDO" && (
          <Typography variant="body2">Recibido el {fmtFecha(d.recibido_at)} por {d.recibido_por || "—"} · recibe: {d.recibe_nombre || "—"}</Typography>
        )}
        {d.observaciones_recepcion && <Typography variant="body2">Observaciones de la recepción: {d.observaciones_recepcion}</Typography>}
        {d.estado === "ANULADO" && <Alert severity="warning" sx={{ mt: 1 }}>Anulado el {fmtFecha(d.anulado_at)}: {d.motivo_anulacion}</Alert>}

        <TableContainer sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Material</TableCell>
                <TableCell>Unidad</TableCell>
                <TableCell align="right">Enviado</TableCell>
                <TableCell align="right" sx={{ width: 150 }}>Recibido</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {d.items.map((i) => (
                <TableRow key={i.id}>
                  <TableCell>{i.codigo} - {i.nombre}</TableCell>
                  <TableCell>{i.unidad}</TableCell>
                  <TableCell align="right">{fmtNum(i.cantidad_enviada)}</TableCell>
                  <TableCell align="right">
                    {puedeRecibir ? (
                      <TextField size="small" type="number" value={cantidadRecibida(i)}
                        onChange={(e) => setRecibidas((prev) => ({ ...prev, [i.id]: e.target.value }))}
                        inputProps={{ min: 0, max: i.cantidad_enviada, step: 1 }} />
                    ) : (i.cantidad_recibida === null ? "—" : fmtNum(i.cantidad_recibida))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {puedeRecibir && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Confirmar recepción</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth size="small" label="Quién recibe (opcional)" value={recibe} onChange={(e) => setRecibe(e.target.value)} />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField fullWidth size="small" label="Observaciones (obligatorias si hay diferencia)" value={obs} onChange={(e) => setObs(e.target.value)} />
              </Grid>
            </Grid>
          </Box>
        )}
        {anulando && (
          <TextField fullWidth size="small" label="Motivo de la anulación" value={motivo} onChange={(e) => setMotivo(e.target.value)} sx={{ mt: 2 }} />
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={descargarActa}>Descargar acta (PDF)</Button>
        {puedeAnular && !anulando && (<Button color="warning" onClick={() => setAnulando(true)}>Anular envío</Button>)}
        {puedeAnular && anulando && (<Button color="warning" variant="contained" onClick={anular} disabled={trabajando}>Confirmar anulación</Button>)}
        {puedeRecibir && (<Button variant="contained" onClick={confirmar} disabled={trabajando}>{trabajando ? "Guardando..." : "Confirmar recepción"}</Button>)}
        <Button onClick={onClose} disabled={trabajando}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

function EnviosTab({ ctx }) {
  const [filtroSuc, setFiltroSuc] = useState("");
  const [estado, setEstado] = useState("");
  const [texto, setTexto] = useState("");
  const q = useDebounced(texto.trim(), 350);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(25);
  const [data, setData] = useState({ items: [], total: 0 });
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [version, setVersion] = useState(0);
  const [nuevo, setNuevo] = useState(false);
  const [abierto, setAbierto] = useState(null);

  useEffect(() => { setPage(0); }, [q, filtroSuc, estado]);

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    setError("");
    axios.get(`${API}/envios`, { params: { sucursal_id: filtroSuc || undefined, estado: estado || undefined, q: q || undefined, page: page + 1, page_size: rows } })
      .then((r) => { if (vivo) setData(r.data); })
      .catch((e) => { if (vivo) { setData({ items: [], total: 0 }); setError(mensajeError(e, "No se pudieron cargar los envíos")); } })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [filtroSuc, estado, q, page, rows, version]);

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
        {ctx.sucursales.length > 1 && (
          <TextField select size="small" label="Sucursal" value={filtroSuc} onChange={(e) => setFiltroSuc(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="">Todas</MenuItem>
            {ctx.sucursales.map((s) => (<MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>))}
          </TextField>
        )}
        <TextField select size="small" label="Estado" value={estado} onChange={(e) => setEstado(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="EN_TRANSITO">En tránsito</MenuItem>
          <MenuItem value="RECIBIDO">Recibidos</MenuItem>
          <MenuItem value="ANULADO">Anulados</MenuItem>
        </TextField>
        <TextField size="small" label="Número de acta" value={texto} onChange={(e) => setTexto(e.target.value)} />
        {ctx.puede_enviar && (<Button variant="contained" onClick={() => setNuevo(true)}>Nuevo envío</Button>)}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {aviso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAviso("")}>{aviso}</Alert>}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Acta</TableCell>
              <TableCell>Destino</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell align="right">Materiales</TableCell>
              <TableCell>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cargando && (<TableRow><TableCell colSpan={5} align="center"><CircularProgress size={20} /></TableCell></TableRow>)}
            {!cargando && data.items.length === 0 && (<TableRow><TableCell colSpan={5} align="center">No hay envíos</TableCell></TableRow>)}
            {!cargando && data.items.map((e) => (
              <TableRow key={e.id} hover sx={{ cursor: "pointer" }} onClick={() => setAbierto(e.id)}>
                <TableCell>{e.numero}</TableCell>
                <TableCell>{e.sucursal_nombre}</TableCell>
                <TableCell>{fmtFecha(e.fecha_envio)}</TableCell>
                <TableCell align="right">{e.items_count}</TableCell>
                <TableCell>
                  <Chip size="small" color={ESTADO_COLOR[e.estado]} label={ESTADO_TEXTO[e.estado] || e.estado} />
                  {e.con_diferencia && <Chip size="small" color="error" variant="outlined" label="Con diferencia" sx={{ ml: 1 }} />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination component="div" count={data.total} page={page} onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rows} onRowsPerPageChange={(e) => { setRows(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage="Filas" />
      </TableContainer>
      {nuevo && (
        <NuevoEnvioDialog ctx={ctx} onClose={() => setNuevo(false)}
          onHecho={(env) => { setNuevo(false); setAviso(`Envío ${env.numero} registrado`); setVersion((v) => v + 1); setAbierto(env.id); }} />
      )}
      {abierto && (
        <DetalleEnvioDialog ctx={ctx} envioId={abierto} onClose={() => setAbierto(null)}
          onCambio={(msg) => { setAviso(msg); setVersion((v) => v + 1); }} />
      )}
    </Box>
  );
}

// ====================== PERSONAL ======================
function PersonalTab({ sucursal }) {
  const [lista, setLista] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [usuario, setUsuario] = useState(null);
  const [rol, setRol] = useState("TECNICO");
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [trabajando, setTrabajando] = useState(false);

  const cargar = () => {
    axios.get(`${API}/${sucursal.id}/personal`)
      .then((r) => setLista(r.data || []))
      .catch((e) => setError(mensajeError(e, "No se pudo cargar el personal")));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setError(""); setAviso(""); cargar(); }, [sucursal.id]);

  useEffect(() => {
    axios.get("/users").then((r) => setUsuarios(r.data || [])).catch(() => setUsuarios([]));
  }, []);

  const rolNecesario = rol === "TECNICO" ? "tecnico_campo" : "bodeguero";
  const elegibles = usuarios.filter((u) => (u.roles || []).some((x) => x.name === rolNecesario));

  const agregar = async () => {
    setError("");
    setAviso("");
    if (!usuario) { setError("Elige un usuario"); return; }
    setTrabajando(true);
    try {
      const r = await axios.post(`${API}/${sucursal.id}/personal`, { user_id: usuario.id, rol });
      setLista(r.data || []);
      setUsuario(null);
      setAviso("Personal actualizado");
    } catch (e) {
      setError(mensajeError(e, "No se pudo agregar al usuario"));
    } finally {
      setTrabajando(false);
    }
  };

  const quitar = async (p) => {
    if (!window.confirm(`¿Quitar a ${p.username} de ${sucursal.nombre}?`)) return;
    setError("");
    setAviso("");
    try {
      const r = await axios.delete(`${API}/${sucursal.id}/personal/${p.user_id}`);
      setLista(r.data || []);
      setAviso("Personal actualizado");
    } catch (e) {
      setError(mensajeError(e, "No se pudo quitar al usuario"));
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Encargado</strong> (usuario con rol bodeguero): recibe los envíos y asigna material a los técnicos de su sucursal y solo ve su sucursal.
        {" "}<strong>Técnico</strong> (rol tecnico_campo): recibe material de esa sucursal. Cada usuario pertenece a una sola sucursal.
      </Alert>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
        <TextField select size="small" label="Rol en la sucursal" value={rol} onChange={(e) => { setRol(e.target.value); setUsuario(null); }} sx={{ minWidth: 190 }}>
          <MenuItem value="TECNICO">Técnico</MenuItem>
          <MenuItem value="ENCARGADO">Encargado</MenuItem>
        </TextField>
        <Autocomplete size="small" sx={{ minWidth: 260 }} options={elegibles} value={usuario}
          getOptionLabel={(u) => u.username || ""} isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_, v) => setUsuario(v)} noOptionsText="Ningún usuario con ese rol"
          renderInput={(params) => (<TextField {...params} label="Usuario" />)} />
        <Button variant="contained" onClick={agregar} disabled={trabajando}>Agregar a {sucursal.nombre}</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {aviso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAviso("")}>{aviso}</Alert>}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow><TableCell>Usuario</TableCell><TableCell>Rol en la sucursal</TableCell><TableCell align="right" /></TableRow>
          </TableHead>
          <TableBody>
            {lista.length === 0 && (<TableRow><TableCell colSpan={3} align="center">Esta sucursal no tiene personal asignado</TableCell></TableRow>)}
            {lista.map((p) => (
              <TableRow key={p.user_id}>
                <TableCell>{p.username}</TableCell>
                <TableCell>{p.rol === "ENCARGADO" ? "Encargado" : "Técnico"}</TableCell>
                <TableCell align="right"><Button size="small" color="warning" onClick={() => quitar(p)}>Quitar</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ====================== PÁGINA ======================
const API_EQ = "/warehouse/sucursales-equipos";

const PESTANAS = [
  { k: "stock", t: "Materiales", Icono: Inventory2Outlined },
  { k: "envios", t: "Envíos de material", Icono: LocalShippingOutlined },
  { k: "envios_eq", t: "Envíos de equipos", Icono: SendOutlined },
  { k: "devoluciones", t: "Devoluciones a la central", Icono: SendOutlined },
  { k: "personal", t: "Personal", Icono: GroupsOutlined },
];

function TarjetaKpi({ Icono, titulo, valor, color, fondo }) {
  return (
    <Paper elevation={0} sx={{ p: 2, display: "flex", alignItems: "center", gap: 2, borderRadius: 3, border: "1px solid #e2e8f0", height: "100%" }}>
      <Box sx={{ width: 46, height: 46, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: fondo, color, flexShrink: 0 }}>
        <Icono />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>{titulo}</Typography>
        <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, lineHeight: 1.15, color: "#0f172a" }}>
          {valor === null || valor === undefined ? "—" : valor}
        </Typography>
      </Box>
    </Paper>
  );
}

export default function SucursalesBodega() {
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("stock");
  const [sucId, setSucId] = useState("");
  const [resumen, setResumen] = useState([]);
  const [kpi, setKpi] = useState({ equipos: null, vinetas: null, transitoEq: null });
  const [recarga, setRecarga] = useState(0);

  useEffect(() => {
    axios.get(`${API}/contexto`)
      .then((r) => {
        setCtx(r.data);
        if (r.data.sucursales.length > 0) setSucId(r.data.sucursales[0].id);
      })
      .catch((e) => setError(mensajeError(e, "No se pudo cargar el módulo de sucursales")));
  }, []);

  useEffect(() => {
    if (!ctx) return;
    axios.get(`${API}/resumen`).then((r) => setResumen(r.data || [])).catch(() => setResumen([]));
  }, [ctx, recarga]);

  useEffect(() => {
    if (!sucId) return undefined;
    let vivo = true;
    setKpi({ equipos: null, vinetas: null, transitoEq: null });
    const p = { page: 1, page_size: 1 };
    Promise.all([
      axios.get(`${API_EQ}/${sucId}/equipos`, { params: p }),
      axios.get(`${API_EQ}/${sucId}/vinetas`, { params: p }),
      axios.get(`${API_EQ}/envios`, { params: { ...p, sucursal_id: sucId, estado: "EN_TRANSITO" } }),
    ])
      .then(([e, v, tr]) => { if (vivo) setKpi({ equipos: e.data.total, vinetas: v.data.total, transitoEq: tr.data.total }); })
      .catch(() => { if (vivo) setKpi({ equipos: 0, vinetas: 0, transitoEq: 0 }); });
    return () => { vivo = false; };
  }, [sucId, recarga]);

  const sucursal = useMemo(() => (ctx ? ctx.sucursales.find((s) => s.id === sucId) || null : null), [ctx, sucId]);
  const envoltura = (hijos) => (<ThemeProvider theme={temaSucursales}>{hijos}</ThemeProvider>);

  if (error) return envoltura(<Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>);
  if (!ctx) return envoltura(<Box sx={{ p: 6, textAlign: "center" }}><CircularProgress /></Box>);
  if (ctx.sucursales.length === 0) {
    return envoltura(<Box sx={{ p: 3 }}><Alert severity="info">No hay sucursales activas. Créalas en Finanzas → Sucursales.</Alert></Box>);
  }

  const filaResumen = resumen.find((r) => r.id === sucId) || {};
  const pestanas = PESTANAS.filter((p) => p.k !== "personal" || ctx.puede_administrar_personal);
  const tabActiva = pestanas.some((p) => p.k === tab) ? tab : "stock";
  const enTransito = (filaResumen.envios_en_transito || 0) + (kpi.transitoEq || 0);
  const rol = ctx.central ? (ctx.admin ? "Administración" : "Bodega central") : `Encargado · ${sucursal ? sucursal.nombre : ""}`;

  return envoltura(
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ position: "relative", overflow: "hidden", borderRadius: 4, p: { xs: 2.5, md: 3.5 }, mb: 3, color: "#fff",
                 background: "linear-gradient(135deg, #1e3a8a 0%, #2563eb 55%, #38bdf8 100%)" }}>
        <Box sx={{ position: "absolute", right: -50, top: -60, width: 220, height: 220, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.10)" }} />
        <Box sx={{ position: "absolute", right: 90, bottom: -80, width: 160, height: 160, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.07)" }} />
        <Box sx={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
          <Box>
            <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 2 }}>Bodega</Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>Sucursales</Typography>
            <Typography sx={{ opacity: 0.92, mt: 1, maxWidth: 680 }}>
              {ctx.central
                ? "La bodega central compra y reparte: envía material, equipos y viñetas a cada sucursal con su acta. Aquí se reciben los envíos, se asigna el material a los técnicos y se administra el personal; los equipos y las viñetas se gestionan en Bodega → Equipos y Viñetas."
                : "Recibe los envíos de la bodega central y asigna material a los técnicos de tu sucursal. Tus equipos y viñetas están en Bodega → Equipos y Viñetas."}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <Chip label={rol} sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 700 }} />
            <Button variant="outlined" startIcon={<RefreshOutlined />} onClick={() => setRecarga((n) => n + 1)}
              sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.55)", "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.12)" } }}>
              Actualizar
            </Button>
          </Box>
        </Box>
      </Box>

      {ctx.sucursales.length > 1 && (
        <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", pb: 1, mb: 2 }}>
          {ctx.sucursales.map((s) => {
            const sel = s.id === sucId;
            const r = resumen.find((x) => x.id === s.id) || {};
            return (
              <Paper key={s.id} elevation={0} onClick={() => setSucId(s.id)}
                sx={{ cursor: "pointer", minWidth: 200, p: 1.75, borderRadius: 3, border: "2px solid", borderColor: sel ? "primary.main" : "#e2e8f0",
                      bgcolor: sel ? "#eff6ff" : "#fff", transition: "all .15s", "&:hover": { borderColor: "primary.light", transform: "translateY(-1px)" } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <StorefrontOutlined fontSize="small" color={sel ? "primary" : "action"} />
                  <Typography sx={{ fontWeight: 700 }} noWrap>{s.nombre}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">Código {s.codigo}</Typography>
                {r.envios_en_transito > 0 && (<Box><Chip size="small" color="warning" label={`${r.envios_en_transito} envío(s) de material en tránsito`} sx={{ mt: 1 }} /></Box>)}
              </Paper>
            );
          })}
        </Box>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md><TarjetaKpi Icono={Inventory2Outlined} titulo="Materiales" valor={filaResumen.materiales_con_stock} color="#2563eb" fondo="#eff6ff" /></Grid>
        <Grid item xs={6} md><TarjetaKpi Icono={RouterOutlined} titulo="Equipos" valor={kpi.equipos} color="#7c3aed" fondo="#f5f3ff" /></Grid>
        <Grid item xs={6} md><TarjetaKpi Icono={LocalOfferOutlined} titulo="Viñetas" valor={kpi.vinetas} color="#0891b2" fondo="#ecfeff" /></Grid>
        <Grid item xs={6} md><TarjetaKpi Icono={LocalShippingOutlined} titulo="En tránsito" valor={filaResumen.envios_en_transito === undefined ? null : enTransito} color="#d97706" fondo="#fffbeb" /></Grid>
        <Grid item xs={12} md><TarjetaKpi Icono={GroupsOutlined} titulo="Personal" valor={filaResumen.personal} color="#16a34a" fondo="#f0fdf4" /></Grid>
      </Grid>

      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        <Tabs value={tabActiva} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
          sx={{ px: 1, borderBottom: "1px solid #e2e8f0", bgcolor: "#fff" }}>
          {pestanas.map(({ k, t: titulo, Icono }) => (
            <Tab key={k} value={k} icon={<Icono fontSize="small" />} iconPosition="start" label={titulo} />
          ))}
        </Tabs>
        <Box sx={{ p: { xs: 1.5, md: 2.5 }, bgcolor: "#fff" }}>
          <Box key={`${tabActiva}-${sucId}-${recarga}`}>
            {tabActiva === "stock" && sucursal && <StockTab sucursal={sucursal} />}
            {tabActiva === "envios" && <EnviosTab ctx={ctx} />}
            {tabActiva === "envios_eq" && <EnviosEquiposPanel ctx={ctx} />}
            {tabActiva === "devoluciones" && <DevolucionesCentralPanel ctx={ctx} />}
            {tabActiva === "personal" && sucursal && <PersonalTab sucursal={sucursal} />}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
